/***************************************************************
 * Copyright (c) 2006 - 2024, Case Anywhere, LLC.
 * All rights reserved
 *
 * @author Brad Wetmore
 * Date: 10/13/2025
 *
 * @version 0.3
 * Revisions:
 * 10/13/2025 - BW - Initial Creation
 * 12/02/2025 - BW - Modified: _isFieldError - simplified logic to only treat client-side validation as
 *                   field errors and show modal for everything else
 * 12/09/2025 - BW - Modified: _setupStripe - added code to not show the "save payment method" as a guest and
 *                             control _$guestAchNote
 *
 * Description/Purpose:
 * Stripe Payment Modal
 ***************************************************************/
import StackTraceError from "component/stack-trace-error/stack-trace-error";
import BannerUtil from "component/banner/banner-util";
import {APP, PAGE_PARAM} from "constant/app-constant";
import ButtonUtil from "util/button-util";
import MODAL from "component/modal/modal-constant";
import {COMMON, TAG_DATA} from "page/payment/stripe-payment-util";
import AjaxUtil from "util/ajax-util";
import ModalUtil from "util/modal-util";
import CommonUtil from "util/common-util";
import {StripePaymentUtil} from "./stripe-payment-util";


const StripePaymentModal = {


  /**
   * Init function
   * @param $form
   * @param options
   * @returns {StripePaymentModal}
   */
  init($form, options) {
    this.$form = $form;
    const defaults = {};
    // extends config
    this.opts = $.extend(true, defaults, options);

    this._setUpVariable();
    this._cacheDom();
    this._registerEvent();
    return this;
  },


  /**
   * Set up some common variables
   * @private
   */
  _setUpVariable() {
    this.stripe = null;
    this.elements = null;
    this.paymentElement = null;
    this.cpIntentDTO = null;
    return this;
  },


  /**
   * Caches some often used DOM objects
   * @returns {StripePaymentModal}
   * @private
   */
  _cacheDom() {
    try {
      this._dataFormLoadedAttribute = APP.DATA_FORM_LOADED_ATTRIBUTE;
      this._modalClassSelector = MODAL.MODAL_CLASS_SELECTOR;
      this.$stripePublishableKey = $(COMMON.STRIPE_PUBLISHABLE_KEY_SELECTOR);
      this.guestDisplay = TAG_DATA.GUEST_DISPLAY;

    } catch (e) {
      StackTraceError.processError(e, 'Stripe Payment Modal - Cache Dom');
    }
    return this;
  },


  /**
   * Register events or DOM event
   */
  _registerEvent() {

    return this;
  },


  /**
   * Init function
   * @param modalWindow
   * @param options
   * @returns {StripePaymentModal}
   */
  initModal(modalWindow, options) {
    try {
      const defaults = {};
      // extends config
      this.opts = $.extend(true, defaults, options);
      this.modalWindow = modalWindow;
      this._modalCacheDom();
      this._registerModalEvent();
      this._setUpModal();

    } catch (e) {
      StackTraceError.processError(e, 'Stripe Payment Modal - Init Modal');
    }
    return this;
  },


  /**
   * Caches modal elements
   * @returns {StripePaymentModal}
   * @private
   */
  _modalCacheDom() {
    try {
      this._modalClassSelector = MODAL.MODAL_CLASS_SELECTOR;
      this._$modalStripePaymentFormContent = $(COMMON.STRIPE_PAYMENT_FORM_CONTENT_SELECTOR);

      this._$urlStripeRedirect = $(COMMON.STRIPE_REDIRECT_URL_SELECTOR);

      this._$stripePaymentAttemptId = $(COMMON.STRIPE_PAYMENT_ATTEMPT_ID_SELECTOR);
      this._$stripePaymentIntentId = $(COMMON.STRIPE_PAYMENT_INTENT_ID_SELECTOR);
      this._$stripeClientSecret = $(COMMON.STRIPE_CLIENT_SECRET_SELECTOR);
      this._$stripePaymentMethodType = $(COMMON.STRIPE_PAYMENT_METHOD_TYPE_SELECTOR);
      this._$stripeCustomerId = $(COMMON.STRIPE_CUSTOMER_ID_SELECTOR);

      this._$organizationNameInfo = $(COMMON.STRIPE_ORGANIZATION_NAME_INFO_SELECTOR);
      this._$firstLastNameInfo = $(COMMON.STRIPE_FIRST_LAST_NAME_INFO_SELECTOR);
      this._$emailAddressInfo = $(COMMON.STRIPE_EMAIL_ADDRESS_INFO_SELECTOR);
      this._$emailAddressInfo = $(COMMON.STRIPE_EMAIL_ADDRESS_INFO_SELECTOR);
      this._$totalBaseInfo = $(COMMON.STRIPE_TOTAL_BASE_INFO_SELECTOR);
      this._$totalFeeInfo = $(COMMON.STRIPE_TOTAL_FEE_INFO_SELECTOR);
      this._$totalChargedInfo = $(COMMON.STRIPE_TOTAL_CHARGED_INFO_SELECTOR);

      this._$stripePaymentMethodTypeTag = $(COMMON.STRIPE_PAYMENT_METHOD_TYPE_TAG_SELECTOR);
      this._$feeNote = $(COMMON.STRIPE_FEE_NOTE_SELECTOR);
      this._$guestAchNote = $(COMMON.STRIPE_GUEST_ACH_NOTE_SELECTOR);

      this._$payButton = $(COMMON.STRIPE_PAYMENT_PAY_BUTTON_SELECTOR);
      this._$cancelButton = $(COMMON.STRIPE_PAYMENT_CANCEL_BUTTON_SELECTOR);

    } catch (e) {
      StackTraceError.processError(e, 'Stripe Payment Modal - Cache Dom');
    }
  },


  /**
   * Register modal events for modal
   * @private
   */
  _registerModalEvent() {
    try {
      this._$cancelButton
          .off('click')
          .on('click',
              ($event) => this.onCancelButtonClick($event));
      this._$payButton
          .off('click')
          .on('click',
              ($event) => this.onPayButtonClick($event));
    } catch (e) {
      StackTraceError.processError(e, 'Stripe Payment Modal - Register Modal Event');
    }
  },


  /**
   * Sets up the modal
   * @subscribe: PUBSUB_EVENT_FILE_UPLOAD_UPLOAD.STRIPE_PAYMENT_MODAL_SET_UP_MODAL
   * @returns {boolean}
   */
  _setUpModal() {
    try {

      //  Send ajax call to get the total row count data
      const request = AjaxUtil.postJsonToAjaxServlet(
          TAG_DATA.URL_PAYMENT_SERVLET,
          TAG_DATA.E_ACT_STRIPE_SETUP_PAYMENT,
          this.$form.serialize(), {},
      );
      request.always((data) => {
        // Always remove the Processing Button
        ButtonUtil.disableProcessingButton(this.$paymentCreditCardButton);
        ButtonUtil.disableProcessingButton(this.$paymentAchButton);
      });
      request.done((dto) => {

        // Setup Stripe
        this.cpIntentDTO = dto.data;
        this._setupStripe();

        // Complete
        this._setUpModalComplete();

      });
      request.fail((jqXHR, textStatus, errorThrown) => {
        StackTraceError.processErrorWithModalFailAlertWithNotifiedMessage(
            new Error(errorThrown), 'Setup Payment - Ajax Fail',
            'An error occurred setting up the Payment',
        );
        // Close the modal
        this.modalWindow.close();
      });

    } catch (e) {
      StackTraceError.processError(e, 'Stripe Modal- Open Modal');
    }

    return true;
  },



  /**
   * Setup Stripe Elements
   * @private
   */
  _setupStripe() {

    const showFees = this.cpIntentDTO.totalFee > 0 && this.cpIntentDTO.paymentMethodType === 'card';

    // First thing is to set the hidden fields with some data from the incoming DTO
    this._$stripePaymentAttemptId.val(this.cpIntentDTO.attemptId);
    this._$stripePaymentIntentId.val(this.cpIntentDTO.paymentIntentId);
    this._$stripeClientSecret.val(this.cpIntentDTO.clientSecret);
    this._$stripePaymentMethodType.val(this.cpIntentDTO.paymentMethodType);
    this._$stripeCustomerId.val(this.cpIntentDTO.customerId);

    this._$organizationNameInfo.html(this.cpIntentDTO.organizationName);
    this._$firstLastNameInfo.html(this.cpIntentDTO.firstLastName);
    this._$emailAddressInfo.html(this.cpIntentDTO.emailAddress);
    this._$totalBaseInfo.html(StripePaymentUtil.formatCurrency(this.cpIntentDTO.totalBase));
    this._$totalFeeInfo.html(StripePaymentUtil.formatCurrency(this.cpIntentDTO.totalFee));
    this._$totalChargedInfo.html(StripePaymentUtil.formatCurrency(this.cpIntentDTO.totalCharged));

    this._$stripePaymentMethodTypeTag.html(
        this.cpIntentDTO.paymentMethodType === 'card' ? 'Credit Card' : 'ACH Bank Transfer');


    // Hide necessary fields if not convenience fee
    if (!showFees) {
      CommonUtil.hide(this._$totalBaseInfo.parent());
      CommonUtil.hide(this._$totalFeeInfo.parent());
      CommonUtil.hide(this._$feeNote);
    }

    // Show Guest ACH Note if necessary
    if (this.guestDisplay && this.cpIntentDTO.paymentMethodType !== 'card') {
      CommonUtil.show(this._$guestAchNote);
    }

    const appearance = {
      theme: 'stripe',
      variables: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontSizeBase: '14px',
        colorText: '#58595b',
        colorTextSecondary: '#5b5b5b',
        //borderRadius: '8px'
      },
      rules: {
        '.Label': {
          fontWeight: '700',
          color: '#58595b'
        },
        '.Input': {
          boxShadow: 'none',
          borderRadius: '10px',
          color: '#58595b',
          spacingUnit: '8px',
        }
      }
    };

    // Init Stripe
    this.stripe = Stripe(this.$stripePublishableKey.val());
    this.elements = this.stripe.elements(
        {
          clientSecret: this.cpIntentDTO.clientSecret,
          customerSessionClientSecret: this.cpIntentDTO.customerSessionClientSecret,
          appearance,
        });

    // Mount the Payment Element
    this._mountPaymentElement();

  },


  /**
   * Mount the Payment Element
   * @private
   */
  _mountPaymentElement() {

    this.paymentElement = this.elements.create('payment', {
      layout: 'tabs',
      defaultValues: {
        billingDetails: {
          name: this.cpIntentDTO.firstLastName || '',
          email: this.cpIntentDTO.emailAddress || '',
        }
      },
      fields: {
        billingDetails: {
          name: 'auto',
          email: 'auto',
        }
      },
      wallets: { link: 'never' },
      paymentMethodSave: this.guestDisplay ? 'never' : 'enabled',
    });
    this.paymentElement.mount(COMMON.STRIPE_PAYMENT_ELEMENT_SELECTOR);

    // Setup Resize Observer to check for overflow on content changes
    const contentEl = this._$modalStripePaymentFormContent[0]; // your inner container
    if (window.ResizeObserver && contentEl) {

      let resizeTimer = null;

      const ro = new ResizeObserver(() => {
        // debounce
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          console.log('[StripeModal] ResizeObserver (debounced) → checking overflow');
          ModalUtil.checkOverflow(this.modalWindow);
        }, 60);   // 50–100 ms sweet spot
      });

      ro.observe(contentEl);
    }
  },


  /**
   * Complete the modal setup
   * @private
   */
  _setUpModalComplete() {

    // Hide Banner
    BannerUtil.hideBanner(COMMON.STRIPE_PAYMENT_LOADING_MODAL_BANNER_SELECTOR);

    // Just to make sure
    // ButtonUtil.enableButton(this._$saveButton);

    // Show the content
    CommonUtil.show(this._$modalStripePaymentFormContent);

    // Check Overflow
    ModalUtil.checkOverflow(this.modalWindow);
  },


  /**
   * Processes the modal "Save" button
   * @param $event
   */
  onPayButtonClick($event) {
    let result = false;
    try {
      if ($event) $event.preventDefault();

      // Show processing
      ButtonUtil.enableProcessingButton(this._$payButton, true, this._modalClassSelector);
      // Build the return URL
      const url = `${this._$urlStripeRedirect.val()}${TAG_DATA.URL_PAYMENT_SERVLET}` +
          `?attempt_id=${this.cpIntentDTO.attemptId}&${PAGE_PARAM.E_ACT}=${TAG_DATA.E_ACT_STRIPE_COMPLETE_PAYMENT}`;

      // Confirm the Payment
      this.elements.submit()
          .then(() => {
            return this.stripe.confirmPayment({
              elements: this.elements,
              clientSecret: this.cpIntentDTO.clientSecret,
              confirmParams: {
                return_url: `${url}`,
                payment_method_data: {
                  billing_details: {
                    name: this.cpIntentDTO.firstLastName,
                    email: this.cpIntentDTO.emailAddress
                  }
                }
              },
              redirect: 'if_required' // I'll just let Stripe redirect me
            })})
          .then(({ paymentIntent, error }) => {
            if (error || !paymentIntent) {
              this._disableButtonStripePayButton();
              if (!this._isFieldError(error)) {
                // If this is not a field error, show the modal alert
                StackTraceError.processErrorWithModalFailAlertWithNotifiedMessage(
                    new Error((error && error.message) || 'An unknown error occurred during payment processing'),
                    'Case Anywhere Payment Error',
                    (error && error.message) || 'An error occurred processing the payment<br><br>' +
                    'Please try again or contact support if the problem persists.',
                    'Case Anywhere Payment Error');
              }
              return; // inline from Stripe or nothing at all if 3ds cancel for example
            }
            const status = paymentIntent.status;
            // Only leave the page when Stripe is finished (card) or has moved ACH forward
            if (status === 'succeeded' || status === 'processing' || status === 'requires_action') {
              // Just call the servlet with the PI info
              window.location.href = `${url}&payment_intent=${encodeURIComponent(paymentIntent.id)}` +
                  `&redirect_status=${encodeURIComponent(paymentIntent.status)}`;
              //this._disableButtonStripePayButton();
              return;
            }
            return;
          })
          .catch(e => {
            StackTraceError.processError(e, 'Case Anywhere Payment - Stripe Confirm Payment');
            throw e;
          });

    } catch (e) {
      this._disableButtonStripePayButton();
      // Catch any other errors
      StackTraceError.processErrorWithModalFailAlertWithNotifiedMessage(
          e, 'Case Anywhere Payment Error',
          'An error occurred processing the payment<br><br>' +
          'Please try again or contact support if the problem persists.', 'Case Anywhere Payment Error'
      );
    }
    return result;
  },


  /**
   * Disables the Stripe Pay Button
   * @private
   */
  _disableButtonStripePayButton() {
    ButtonUtil.disableProcessingButton(this._$payButton, true, false, this._modalClassSelector);
  },


  /**
   * Determines if the error is a field error
   * @param error
   * @returns {boolean}
   * @private
   */
  _isFieldError(error) {
    if (!error) return false;

    // Only treat client-side validation as field errors
    if (error.type === 'validation_error' && !!error.param) {
      return true;           // bad card number, missing expiry, etc.
    }

    // Everything else (all card_error / gateway stuff) -> NOT field-only
    return false;
    //const NON_FIELD_DECLINE_CODES = new Set(['test_mode_live_card', 'gateway_rejected', 'fraudulent']);
    // if (!error) return false;

    // if (error.decline_code && NON_FIELD_DECLINE_CODES.has(error.decline_code)) {
    //   return false;
    // }

    // return (['validation_error','card_error'].includes(error.type) || !!error.param);
  },


  /**
   * Processes the modal "Cancel" button
   * @param $event
   */
  onCancelButtonClick($event) {
    this.modalWindow.close();
  },


};

export default StripePaymentModal;
